import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import useConstant from 'use-constant';
import awesomeDebouncePromise from 'awesome-debounce-promise';
import {useAsync} from 'react-async';
import {useDispatch, useSelector} from 'react-redux';
import {
  fetchPokusDataStatusSelectorFn,
  pokusAutosuggestExpValueSelectorFn,
  pokusAutosuggestChildExpValueSelectorFn,
  newCityNameSelectorFn,
  selectedPropertyNameSelector,
  requestQueryParamSelector,
} from '@stencil/hotels-common-reducer/src/selectors';
import {trendingDestinationsSelectorFnV2} from '../../../../../../../../../hotels/hotels-reducers/hotel-destinations-reducer/src/selectors';
import {FetchStatusType} from '@stencil/hotels-types/src/details-types';

// 🐨 put the below line at the entry point of webpack for shared folder
// import * as styledcomponenttypes from 'styled-components/cssprop';

// COMPONENTS and HOOKS
import Text from '../../../../../core-components/TextFieldExpt/TextFieldExpt';
import useAutosuggest from '@stencil/use-autosuggest';
import AutosuggestDropdownNew from '../Components/AutosuggestDropdownNew';

// STYLES
import * as styles from './SearchWidgetAutosuggest.styles';

import * as S from '../HomePageAutosuggest/HomePageAutosuggest.styles';

// UTILS and CONSTANTS
import {itemToString} from '../utils';
import {
  updateAutoSuggestSelection,
  modifyNewAutoSuggestResponse,
} from '@stencil/hotels-utils/src/autoSuggest-utils/parser';

// APP CONFIG
import {DEFAULT_ERROR_MESSAGE, DEBOUNCE_TIMEOUT} from '../constants';
import {getPlacesId} from '../HomePageAutosuggest/utils';
import usePrevious from '../../../../../../shared/hooks/usePrevious';

import fetchVoyagerAutoSuggestions from '@stencil/hotels-utils/src/autoSuggest-utils/fetchVoyagerAutoSuggestions';
import {getNewAutoSuggestUrl} from '@stencil/hotels-utils/src/autoSuggest-utils/getAutoSuggestUrl';
import useNewAutoSuggestLoad from '@stencil/hotels-hooks/src/useNewAutoSuggestLoad';
import getPlaceDetailsFromCoordinatesUnif from '@stencil/hotels-cg-api/src/get-place-details-from-coordinates';
import {fetchDestinationsThunkV2} from '../../../../../../../../../hotels/hotels-reducers/hotel-destinations-reducer/src/thunks';

const fetchVoyagerLocationSuggestions = async ({inputValue, getUrl}, {signal}) => {
  if (!inputValue) {
    return [];
  }

  let data = fetchVoyagerAutoSuggestions({inputValue}, {signal}, getUrl);
  return data;

  /** 🐨 handle gracefully if voyager doesn't return ret.data.r an array */
};

function SearchWidgetAutosuggest({
  initialInputValue,
  onItemSelection,
  destinationChanged,
  isDetail,
  isSRPSEOV2 = false,
}) {
  const previousInitialValue = usePrevious(initialInputValue);
  const pokusDataStatus = useSelector(fetchPokusDataStatusSelectorFn);
  const autosuggestChildExpValue = useSelector(pokusAutosuggestChildExpValueSelectorFn);
  const autosuggestExpValue = useSelector(pokusAutosuggestExpValueSelectorFn);
  const trendingDestinations = useSelector(trendingDestinationsSelectorFnV2) || [];
  const cityNameInStore = useSelector(newCityNameSelectorFn);
  const propertyName = useSelector(selectedPropertyNameSelector);
  const requestQuery = useSelector(requestQueryParamSelector);

  const outerSearchRef = useRef(null);
  const inputRef = useRef(null);

  const dispatch = useDispatch();

  const getNewUrl = search_query => {
    let url = getNewAutoSuggestUrl({
      search_query,
      exps: autosuggestExpValue,
      expKey: autosuggestChildExpValue,
    });
    return url;
  };

  const voyagerAutoSuggestionsProps = useMemo(
    () => ({
      params: {inputValue: '', isByPassBlankInput: true},
      getNewUrl,
    }),
    [],
  );

  const cityName = useMemo(() => {
    let name = cityNameInStore;
    if (isSRPSEOV2 && requestQuery?.searchText) {
      name = requestQuery?.searchText;
    }
    return name;
  }, [isSRPSEOV2, cityNameInStore]);

  const fetchTrendingDestinationsThunkCaller = useCallback(() => {
    return dispatch(fetchDestinationsThunkV2(voyagerAutoSuggestionsProps));
  }, [dispatch]);

  const [startWatch, setStartWatch] = useState(false);

  const getLocationData = async suggestion => {
    const locationDataUnif = await getPlaceDetailsFromCoordinatesUnif({
      placeId: suggestion?.place_id,
    });

    const search = locationDataUnif?.details?.search || {};
    const location = locationDataUnif?.location || {};

    suggestion.locus = {id: search?.id, type: search?.type, cityCode: search?.id};
    suggestion.crd = [location?.latitude, location?.longitude];
    suggestion.isGooglePlace = true;
    suggestion.xtr.cc = locationDataUnif?.details?.country?.id;

    return suggestion;
  };

  const {
    actions: {inputHandleOverride},
    propGetters: {getInputProps, getMenuProps, getItemProps, getRootProps, getLabelProps},
    state: {isOpen, highlightedIndex, selectedItem, inputValue},
  } = useAutosuggest({
    itemToString,
    initialInputValue,
    async onSelectedItemChange(changes) {
      let updatedSuggestion = changes.selectedItem || {};
      updatedSuggestion = updateAutoSuggestSelection({...updatedSuggestion, dummyId: 'dummy'});
      if (updatedSuggestion?._id === 'dummy' || !updatedSuggestion?._id) {
        try {
          updatedSuggestion = await getLocationData(updatedSuggestion);
        } catch (err) {
          console.error(err);
          // opportunity for toast message
        }
      }
      inputRef.current.blur();
      onItemSelection({...changes, selectedItem: updatedSuggestion}, inputValue, highlightedIndex);
    },
  });

  const getUrl = useCallback(
    search_query => {
      return getNewAutoSuggestUrl({
        search_query,
        exps: autosuggestExpValue,
        expKey: autosuggestChildExpValue,
      });
    },
    [autosuggestExpValue, autosuggestChildExpValue],
  );

  const setdestNameonBlur = useCallback(() => {
    if (propertyName) inputHandleOverride(propertyName);
    else inputHandleOverride(cityName);
  }, [cityName, propertyName]);

  useEffect(() => {
    if (!!previousInitialValue && previousInitialValue != initialInputValue) {
      inputHandleOverride(initialInputValue);
    }
  }, [destinationChanged]);

  useEffect(() => {
    const isPokusDataResolvedorRejected =
      pokusDataStatus === FetchStatusType.RESOLVED || pokusDataStatus === FetchStatusType.REJECTED;
    if (isPokusDataResolvedorRejected) {
      setStartWatch(true);
    }
  }, [pokusDataStatus]);

  useEffect(() => {
    const handleClickOutside = event => {
      const isClickOutside =
        outerSearchRef.current && !outerSearchRef?.current?.contains(event.target);
      const isItemNotSelected = !selectedItem || selectedItem.displayName !== cityName;

      if (!inputValue && isClickOutside && isItemNotSelected) {
        setdestNameonBlur();
      }
    };

    document.addEventListener('click', handleClickOutside); // Detect outside clicks

    return () => {
      document.removeEventListener('click', handleClickOutside); // Clean up the event listener
    };
  }, [selectedItem, cityName, outerSearchRef.current]);

  const trendingDestinationsCaller = () => {
    if (!trendingDestinations.length) fetchTrendingDestinationsThunkCaller();
  };

  const debouncedVoyagerFetchPromise = useConstant(() =>
    awesomeDebouncePromise(fetchVoyagerLocationSuggestions, DEBOUNCE_TIMEOUT),
  );

  // @ts-ignore
  const {
    data = [],
    isPending,
    isRejected,
    error,
  } = useAsync({
    promiseFn: startWatch && debouncedVoyagerFetchPromise,
    watch: inputValue === initialInputValue ? '' : inputValue,
    inputValue: inputValue === initialInputValue ? '' : inputValue,
    getUrl,
  });

  const items = modifyNewAutoSuggestResponse(data);

  useNewAutoSuggestLoad({
    flavour: 'dweb',
    data: items,
  });

  const props = {
    items,
    isOpen,
    getMenuProps,
    getItemProps,
    highlightedIndex,
    selectedItem,
    menuStyle: {top: '6rem', left: '0'},
    inputValue,
    showPopularDestinations: !inputValue && trendingDestinations.length,
    popularDestinations: trendingDestinations,
  };

  return (
    // @ts-ignore
    <styles.AutocompleteWrapperStyles {...getRootProps()} ref={outerSearchRef}>
      <styles.SearchLabelStyles {...getLabelProps()}>
        AREA, LANDMARK OR PROPERTY NAME
      </styles.SearchLabelStyles>
      <styles.SearchInputStyles
        {...getInputProps({
          // @ts-ignore
          ref: inputRef,
          placeholder: 'LOCATION NAME',
          // value: (value && value.n) || 'LOCATION NAME',
          onFocus: () => {
            inputHandleOverride('');
            if (!isDetail) trendingDestinationsCaller();
          },
          onBlur: () => {
            setdestNameonBlur();
          },
        })}
      />

      {isRejected && (error || DEFAULT_ERROR_MESSAGE) && (
        <S.ErrorMessage>
          <Text align="left" bold>
            {error}
          </Text>
        </S.ErrorMessage>
      )}

      <AutosuggestDropdownNew {...props} />
    </styles.AutocompleteWrapperStyles>
  );
}

export default SearchWidgetAutosuggest;
